import logoDark from "@public/logo.svg";
import logoLight from "@public/logo_on_light.svg";
import type { Translated } from "@repo/shared/types";
import { Suspense, useState, useTransition } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { OAuthConsentAccount } from "@/components/auth/oauth-consent-account";
import { OAuthConsentClientCard } from "@/components/auth/oauth-consent-client-card";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { authClient } from "@/lib/auth-client";
import { useOAuthConsentClient } from "@/lib/hooks/use-security";
import { useT } from "@/lib/i18n/use-t";
import { isPermanentQueryError } from "@/providers/query-provider";
import { T } from "./page.i18n";

// Map OAuth scopes to user-friendly descriptions
const getScopeDescription = (scope: string, t: Translated<typeof T>): string => {
  const scopeMap: Record<string, keyof typeof T.en_GB> = {
    openid: "scopeOpenid",
    profile: "scopeProfile",
    email: "scopeEmail",
    offline_access: "scopeOfflineAccess",
  };
  const key = scopeMap[scope];
  return key ? t[key] : scope;
};

function ConsentPageContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const t = useT(T);
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [consentResult, setConsentResult] = useState<"approved" | "denied" | null>(null);

  // The authorize endpoint signs the OAuth request into this page's query (`sig`);
  // the auth client sends it back with the consent so the request can be resumed.
  const signature = searchParams.get("sig");
  const clientId = searchParams.get("client_id");
  const redirectUri = searchParams.get("redirect_uri");
  const scope = searchParams.get("scope") || "";

  const scopes = scope.split(" ").filter(Boolean);
  // Both come from the signed query, so the host shown is the one the code is sent to:
  // a tampered redirect_uri fails the signature check when the consent is submitted.
  const {
    data: client,
    error: clientError,
    isPending: isClientPending,
    isFetching: isClientFetching,
    refetch: refetchClient,
  } = useOAuthConsentClient(clientId, redirectUri);
  const appName = client?.name ?? t.theApp;

  const handleConsent = async (accept: boolean) => {
    if (!signature) {
      setError(t.missingParams);
      return;
    }

    setError(null);

    startTransition(async () => {
      try {
        const { data, error: consentError } = await authClient.oauth2.consent({ accept });
        if (consentError || !data) {
          setError(consentError?.message || t.authError);
          return;
        }

        // Show success/denied state first
        setConsentResult(accept ? "approved" : "denied");

        // Better Auth returns the client's callback URL (with the code, or the
        // access_denied error). Redirect in background after showing the result.
        const redirectUrl =
          (data as { redirect_uri?: string; url?: string }).redirect_uri ??
          (data as { url?: string }).url;
        if (redirectUrl) {
          // Small delay so user sees the success message
          setTimeout(() => {
            if (redirectUrl.startsWith("http://") || redirectUrl.startsWith("https://")) {
              window.location.href = redirectUrl;
            } else {
              navigate(redirectUrl);
            }
          }, 100);
        }
      } catch {
        setError(t.authError);
      }
    });
  };

  // Show error if missing required params
  if (!signature || !clientId || !redirectUri) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md p-8">
          <div className="text-center">
            <div className="text-destructive mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <p className="text-muted-foreground">{t.missingParams}</p>
          </div>
        </Card>
      </div>
    );
  }

  // Show success state after approval
  if (consentResult === "approved") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md p-8">
          <div className="text-center">
            <div className="text-success mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold mb-2">{t.authComplete}</h1>
            <p className="text-muted-foreground">
              {t.canCloseTab} {appName}.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Show denied state
  if (consentResult === "denied") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md p-8">
          <div className="text-center">
            <div className="text-muted-foreground mb-4">
              <svg
                className="w-16 h-16 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-bold mb-2">{t.accessDenied}</h1>
            <p className="text-muted-foreground">{t.deniedMessage}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <Card className="w-full max-w-md p-8">
        <div className="mb-6 text-center">
          <div className="flex justify-center mb-4">
            <img
              src={logoLight}
              alt="Quantfolio Platform"
              width={200}
              height={62}
              className="dark:hidden"
            />
            <img
              src={logoDark}
              alt="Quantfolio Platform"
              width={200}
              height={62}
              className="hidden dark:block"
            />
          </div>
          <h1 className="text-xl font-bold">{t.authorizeApp}</h1>
        </div>

        {client ? (
          <OAuthConsentClientCard client={client} />
        ) : isClientPending ? (
          <Skeleton className="mb-6 h-32 w-full" />
        ) : (
          <div
            role="alert"
            className="mb-6 text-sm text-destructive bg-destructive/10 p-3 rounded-md"
          >
            <p>{t.clientLookupFailed}</p>
            {/* A 4xx (the client is unknown or the redirect not registered) never changes on
                retry; the button is for network errors and 5xx only. */}
            {!isPermanentQueryError(clientError) && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetchClient()}
                disabled={isClientFetching}
              >
                {t.tryAgain}
              </Button>
            )}
          </div>
        )}

        <OAuthConsentAccount searchParams={searchParams} />

        {/* Only one alert at a time: when the client lookup itself failed, that box
            above already explains why nothing can be approved — don't also show a
            submission error (e.g. from a Deny click, which doesn't need `client`). */}
        {error && !clientError && (
          <div
            role="alert"
            className="mb-4 text-sm text-destructive bg-destructive/10 p-3 rounded-md"
          >
            {error}
          </div>
        )}

        {scopes.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-medium mb-3">{t.requestedPermissions}</h2>
            <ul className="space-y-2">
              {scopes.map((s) => (
                <li key={s} className="flex items-center gap-2 text-sm">
                  <svg
                    className="w-4 h-4 text-primary flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span>{getScopeDescription(s, t)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleConsent(false)}
            disabled={isPending}
          >
            {t.deny}
          </Button>
          <Button
            className="flex-1"
            onClick={() => handleConsent(true)}
            disabled={!client}
            loading={isPending}
          >
            {t.approve}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// Wrap in Suspense for useSearchParams (required in Next.js App Router)
export default function ConsentPage() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
              <div className="h-24 bg-muted rounded" />
              <div className="h-10 bg-muted rounded" />
            </div>
          </Card>
        </div>
      }
    >
      <ConsentPageContent />
    </Suspense>
  );
}
